import { QueryClient, useMutation, useQuery } from "@tanstack/react-query"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { useContext, useEffect, useMemo, useState } from "react"
import { UserContext } from "../../context/UserContext"
import { Button, Card, CardActions, CardContent, CardHeader, List, ListItem, TextField, Container, Box, Typography, Divider, Paper, Grid } from "@mui/material"
import { PostMedia } from "../../components/post/card/PostMedia"
import { useNavigate, useParams } from "react-router"
import { editForum, getForum } from "../../controllers/forum/forum"
import { CategoryPicker } from "../../components/category.picker"
import { editComment } from "../../controllers/posts/comments"
import { getSinglePost } from "../../controllers/posts/posts"
import { Delete, Forum, Category, Edit } from "@mui/icons-material"

export const EditForum = () => {
    const { userDetails } = useContext(UserContext)
    const [media_ids, setMediaIds] = useState([])
    const [imageUrls, setImageUrls] = useState([])
    const [category, setCategory] = useState('')
    const [status, setStatus] = useState('')
    let navigate = useNavigate()
    const { id } = useParams()
    let token = userDetails?.token
    const { data:forum, refetch } = useQuery({ queryKey: ['getSingleForum'], queryFn: () => getForum(token, id) })
 
    let single_forum = useMemo(() => forum?.forum ?? {} , [forum])

    const { data:content, refetch:refetchPost } = useQuery({ queryKey: ['getSinglePost'], queryFn: () => getSinglePost(single_forum?.status, token) })
    
    useEffect(() => {
        refetch()
        refetchPost()
    })
    
    useEffect(() => {
        let img_urls = []
        content?.media_attachments?.filter(item => img_urls.push(item.url))
        setImageUrls(img_urls)
    }, [content])
    
    useEffect(() => {
      setStatus(single_forum?.title)
      single_forum?.category && setCategory(single_forum?.category)
    }, [single_forum])

    const queryClient = new QueryClient()

    const mutationComment = useMutation({
      mutationFn: editComment,
      onSuccess: (data) => {
        // Invalidate and refetch
        console.log(data)
        queryClient.invalidateQueries({ queryKey: ['editComment'] })
        navigate("/forum/show/"+single_forum?._id)
      },
    })
  
    const send_edit = () => {
        mutationComment.mutate({
        id:single_forum?.status,
        body:{
            status,
            media_ids
        },
        token
      })
    }
    const mutation = useMutation({
        mutationFn: editForum,
        onSuccess: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['editForum'] })
        send_edit()
        },
    })
    
    const sendEditedForum = () => { 
        mutation.mutate({
            id:single_forum?._id,
            body:{
                title:status,
                category
            },
            token:userDetails?.token
        })
    }

    return (
        <MainLoggedInLayout>
            {single_forum?.author?.includes(userDetails?.id) ? (
                <Container maxWidth="lg" sx={{ py: 3 }}>
                    <Card sx={{ 
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}>
                        <CardHeader 
                            title={
                                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    Edit Forum Topic
                                </Typography>
                            }
                            subheader={
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                    Update your forum topic and discussion details
                                </Typography>
                            }
                            action={
                                <Button 
                                    variant="outlined" 
                                    color="error" 
                                    startIcon={<Delete />}
                                    onClick={() => navigate("/forum/delete/"+single_forum?._id)}
                                    sx={{ 
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Delete Topic
                                </Button>
                            }
                            sx={{ pb: 1 }}
                        />
                        
                        <CardContent sx={{ p: 4 }}>
                            <Grid container spacing={4}>
                                {/* Topic Content */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                            <Edit sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                Topic Content
                                            </Typography>
                                        </Box>
                                        
                                        <TextField 
                                            value={status} 
                                            onChange={(e) => setStatus(e.target.value)} 
                                            fullWidth 
                                            multiline 
                                            rows={4}
                                            label='Edit Topic Title & Description' 
                                            variant="outlined"
                                            sx={{ mb: 3 }}
                                        />
                                        
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                                Attachments
                                            </Typography>
                                            <PostMedia 
                                                imageUrls={imageUrls} 
                                                setImageUrls={setImageUrls} 
                                                media_ids={media_ids} 
                                                setMediaIds={setMediaIds} 
                                                token={token} 
                                            />
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* Category */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Category sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                Topic Category
                                            </Typography>
                                        </Box>
                                        <CategoryPicker category={category} setCategory={setCategory} />
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                        
                        <Divider />
                        
                        <CardActions sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="outlined" 
                                        onClick={() => navigate("/forum/show/"+single_forum?._id)}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="contained" 
                                        onClick={sendEditedForum}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Update Topic
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardActions>
                    </Card>
                </Container>
            ) : (
                <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                    <Paper sx={{ p: 4, borderRadius: 3 }}>
                        <Typography variant="h5" color="error" gutterBottom>
                            Access Denied
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            You don't have permission to edit this forum topic. Only the topic author can make changes.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => navigate("/forum/list")}
                            sx={{ 
                                textTransform: 'none',
                                fontWeight: 'bold',
                                borderRadius: 2
                            }}
                        >
                            Browse Forum Topics
                        </Button>
                    </Paper>
                </Container>
            )}
        </MainLoggedInLayout>
    )
}